/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Shows the make/model image from the aegis-aa blob (model-pictures container, copied from the
 * rental storage), falling back to the car's own image, then a placeholder. Filename convention
 * mirrors the source: {MAKE}_{MODEL}.png  (uppercased, spaces in the model become underscores).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MODEL_IMG_BASE =
  (import.meta as any).env?.VITE_MODEL_IMG_BASE ||
  'https://aegisagblob.blob.core.windows.net/model-pictures';

export function modelImageUrl(make?: string, model?: string): string | null {
  const mk = (make || '').trim();
  const md = (model || '').trim();
  if (!mk || !md) return null;
  const file = `${mk.toUpperCase()}_${md.toUpperCase().replace(/\s+/g, '_')}.png`;
  return `${MODEL_IMG_BASE}/${encodeURIComponent(file)}`;
}

/** Generic "unknown car" placeholder image (in the same blob container). */
export const UNKNOWN_CAR_URL = `${MODEL_IMG_BASE}/UNKNOWN_CAR.png`;

interface Props {
  make?: string;
  model?: string;
  fallback?: string; // the car's own image
  alt?: string;
  className?: string;
  /** Try the car's own photo first and use the model image as the fallback (per-car cards). */
  carImageFirst?: boolean;
}

export default function ModelImage({ make, model, fallback, alt, className, carImageFirst }: Props) {
  const { t } = useTranslation();
  const ordered = carImageFirst
    ? [fallback, modelImageUrl(make, model)]
    : [modelImageUrl(make, model), fallback];
  // Always end on the generic UNKNOWN_CAR placeholder so there's never an empty image.
  const candidates = [...(ordered.filter(Boolean) as string[]), UNKNOWN_CAR_URL];
  const [idx, setIdx] = useState(0);

  // Reset to the first candidate when inputs change.
  useEffect(() => setIdx(0), [make, model, fallback]);

  if (idx >= candidates.length) {
    return (
      <div className={`${className ?? ''} flex items-center justify-center text-gray-300 text-sm`}>
        {t('card.noPhoto')}
      </div>
    );
  }

  return (
    <img
      src={candidates[idx]}
      alt={alt}
      className={className}
      onError={() => setIdx((i) => i + 1)}
    />
  );
}
